/* eslint-disable no-undef */
/* global Temporal */
import React, { useEffect, useRef, useState } from 'react';
import { createCalendar, createViewMonthGrid, createViewWeek, createViewDay, createViewMonthAgenda } from '@schedule-x/calendar';
import { createTimezoneSelectPlugin, translations as timezoneTranslations } from '@schedule-x/timezone-select';
import { translations, mergeLocales } from '@schedule-x/translations';
import '@schedule-x/theme-default/dist/index.css';
import '@schedule-x/timezone-select/index.css';
import 'temporal-polyfill/global';
import { Box, Typography } from '@mui/material';
import { statusToCalendarId } from '../dummyOperations';

/**
 * Extracts date and time from Schedule-X calendar click events
 * and formats them for the EventCreateModal
 */
const extractDateTimeFromCalendarClick = (dateTimeData) => {
    try {
        console.log('[DEBUG] Raw dateTimeData from calendar:', dateTimeData);
        console.log('[DEBUG] Type:', typeof dateTimeData);
        console.log('[DEBUG] Keys (if object):', typeof dateTimeData === 'object' ? Object.keys(dateTimeData || {}) : 'N/A');
        
        if (!dateTimeData || (typeof dateTimeData === 'object' && Object.keys(dateTimeData).length === 0)) {
            console.log('[DEBUG] No valid dateTimeData, using current date/time');
            const now = new Date();
            const currentDate = now.toISOString().split('T')[0];
            const currentTime = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
            return `${currentDate} ${currentTime}`;
        }

        if (typeof dateTimeData === 'string') {
            if (dateTimeData.includes('T')) {
                const [datePart, timePart] = dateTimeData.split('T');
                const timeOnly = timePart.split(':').slice(0, 2).join(':');
                return `${datePart} ${timeOnly}`;
            } else if (dateTimeData.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const currentTime = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                return `${dateTimeData} ${currentTime}`;
            } else if (dateTimeData.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/)) {
                return dateTimeData;
            }
        }

        if (dateTimeData && typeof dateTimeData === 'object') {
            if (dateTimeData.year && dateTimeData.month && dateTimeData.day) {
                const year = dateTimeData.year;
                const month = String(dateTimeData.month).padStart(2, '0');
                const day = String(dateTimeData.day).padStart(2, '0');
                const hour = String(dateTimeData.hour || new Date().getHours()).padStart(2, '0');
                const minute = String(dateTimeData.minute || new Date().getMinutes()).padStart(2, '0');
                return `${year}-${month}-${day} ${hour}:${minute}`;
            }
        }

        const now = new Date();
        return `${now.toISOString().split('T')[0]} ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    } catch (error) {
        const now = new Date();
        return `${now.toISOString().split('T')[0]} ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    }
};

/**
 * OperationCalendarView
 *
 * Renders a Schedule-X calendar showing drone operations as color-coded events.
 * Uses the built-in @schedule-x/timezone-select plugin — the timezone dropdown
 * appears natively inside the calendar's own header UI.
 *
 * Props:
 *   operations  — array of operation objects
 *   onEventClick — (operation) => void
 */
const OperationCalendarView = ({ operations = [], onEventClick, onBackgroundClick }) => {
    const calendarRef = useRef(null);
    const calendarInstanceRef = useRef(null);
    const operationsMapRef = useRef({});
    const tooltipRef = useRef(null);
    const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: null });

    const getCurrentDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const getCurrentTime = () => {
        return new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    };

    // Tooltip functions
    const showTooltip = (mouseEvent, operation) => {
        console.log('[DEBUG] showTooltip called for operation:', operation.operation_id, 'at position:', mouseEvent.clientX, mouseEvent.clientY);

        const tooltipContent = {
            flightZone: `FLZ${operation.operation_id}`,
            status: operation.status,
            startTime: `${operation.start_time}${operation.timezone ? ' (' + operation.timezone + ')' : ''}`,
            duration: `${operation.duration || 30} minutes`,
            location: operation.address || (operation.latitude && operation.longitude
                ? `${Math.abs(operation.latitude).toFixed(4)}°${operation.latitude >= 0 ? 'N' : 'S'}, ${Math.abs(operation.longitude).toFixed(4)}°${operation.longitude >= 0 ? 'E' : 'W'}`
                : 'Location not specified'),
            pilot: operation.pilot || 'Not assigned',
            aircraft: operation.aircraft || 'Not specified',
            description: operation.description || 'No description'
        };

        // Calculate tooltip position with viewport boundary checks
        const tooltipWidth = 320;
        const tooltipHeight = 200;
        const offset = 15;

        let x = mouseEvent.clientX + offset;
        let y = mouseEvent.clientY - offset;

        // Check right boundary
        if (x + tooltipWidth > window.innerWidth) {
            x = mouseEvent.clientX - tooltipWidth - offset;
        }

        // Check bottom boundary
        if (y + tooltipHeight > window.innerHeight) {
            y = mouseEvent.clientY - tooltipHeight - offset;
        }

        // Check left boundary
        if (x < 0) {
            x = offset;
        }

        // Check top boundary
        if (y < 0) {
            y = mouseEvent.clientY + offset;
        }

        console.log('[DEBUG] Setting tooltip position:', x, y);
        setTooltip({
            visible: true,
            x: x,
            y: y,
            content: tooltipContent
        });
    };

    const hideTooltip = () => {
        console.log('[DEBUG] hideTooltip called');
        setTooltip({ visible: false, x: 0, y: 0, content: null });
    };

    // Build a map of id → operation for fast lookup on event click
    useEffect(() => {
        const map = {};
        operations.forEach(op => {
            map[String(op.id)] = op;
        });
        operationsMapRef.current = map;
    }, [operations]);

    useEffect(() => {
        if (!calendarRef.current) return;

        // --- Timezone abbreviation → IANA ID lookup ---
        // Temporal.ZonedDateTime needs a proper IANA timezone ID ('America/New_York'),
        // not abbreviations like 'EST'. Falls back to UTC for unknown zones.
        const TZ_MAP = {
            // US
            'EST': 'America/New_York', 'EDT': 'America/New_York', 'ET': 'America/New_York',
            'CST': 'America/Chicago', 'CDT': 'America/Chicago', 'CT': 'America/Chicago',
            'MST': 'America/Denver', 'MDT': 'America/Denver', 'MT': 'America/Denver',
            'PST': 'America/Los_Angeles', 'PDT': 'America/Los_Angeles', 'PT': 'America/Los_Angeles',
            'AKT': 'America/Anchorage', 'HST': 'Pacific/Honolulu',
            // International
            'GMT': 'Etc/GMT', 'UTC': 'UTC',
            'IST': 'Asia/Kolkata', 'BST': 'Europe/London',
            'CET': 'Europe/Paris', 'CEST': 'Europe/Paris',
            'EET': 'Europe/Helsinki', 'JST': 'Asia/Tokyo',
            'AEST': 'Australia/Sydney', 'AEDT': 'Australia/Sydney',
            'NZST': 'Pacific/Auckland',
        };
        const resolveTimezone = (tz) => {
            if (!tz) return 'UTC';
            if (tz.includes('/')) return tz; // already an IANA ID
            return TZ_MAP[tz.toUpperCase().trim()] ?? 'UTC';
        };

        // --- Build Schedule-X event objects ---
        // Schedule-X requires Temporal.ZonedDateTime or Temporal.PlainDate for start/end
        console.log('[DEBUG] Temporal availability check:', {
            TemporalExists: typeof Temporal !== 'undefined',
            ZonedDateTimeExists: typeof Temporal?.ZonedDateTime !== 'undefined',
            PlainDateExists: typeof Temporal?.PlainDate !== 'undefined',
            nowWorks: (() => {
                try {
                    return Temporal?.Now?.zonedDateTimeISO?.()?.toString();
                } catch (e) {
                    return `Error: ${e.message}`;
                }
            })()
        });
        const events = operations.reduce((acc, op) => {
            try {
                let status = op.is_note ? 'Note' : (op.authorizations?.length > 0
                    ? op.authorizations[0].status
                    : 'No Authorization');

                const calId = statusToCalendarId(status);

                // Console log the operation data for debugging
                console.log('[DEBUG] Processing operation for calendar:', {
                    id: op.id,
                    operation_id: op.operation_id,
                    date: op.date,
                    start_time: op.start_time,
                    duration: op.duration,
                    timezone: op.timezone
                });

                // Resolve IANA timezone from the operation's timezone field
                const ianaZone = resolveTimezone(op.timezone);
                console.log('[DEBUG] Timezone resolution:', {
                    original: op.timezone,
                    resolved: ianaZone
                });

                // Sanitize date (extract YYYY-MM-DD if it's a full ISO string)
                const safeDate = op.date ? op.date.split('T')[0] : '';
                // Sanitize start time (extract HH:mm if it includes seconds)
                const safeTime = op.start_time ? op.start_time.substring(0, 5) : '00:00';

                // Build ISO 8601 strings with the resolved IANA timezone for the start
                const startISO = `${safeDate}T${safeTime}:00[${ianaZone}]`;
                const startZDT = Temporal.ZonedDateTime.from(startISO);
                
                // Compute end time using Temporal to correctly handle midnight crossing and daylight savings
                const durationMins = Number(op.duration) || 30;
                const endZDT = startZDT.add({ minutes: durationMins });

                console.log('[DEBUG] Time calculations:', {
                    originalTime: op.start_time,
                    safeTime,
                    durationMins,
                    startString: startZDT.toString(),
                    endString: endZDT.toString()
                });

                // Format location as concise coordinate string
                const lat = op.latitude != null
                    ? `${Math.abs(op.latitude).toFixed(2)}°${op.latitude >= 0 ? 'N' : 'S'}`
                    : null;
                const lon = op.longitude != null
                    ? `${Math.abs(op.longitude).toFixed(2)}°${op.longitude >= 0 ? 'E' : 'W'}`
                    : null;
                const locationStr = (lat && lon) ? `${lat}, ${lon}` : (op.address || '');

                // Build title: If user created task with description, show description prominently
                // Otherwise, show flightZone + technical details for mission logs
                let displayTitle;
                if (op.is_note) {
                    displayTitle = `${op.description?.trim() || 'Reminder'} • Note`;
                } else if (op.description && op.description.trim() && !op.flightZone) {
                    // User-created task: show description as main title
                    displayTitle = `${op.description.trim()} • Note`;
                } else {
                    // Mission log or task with flightZone: show technical details
                    const titleLabel = op.flightZone || `FLZ${op.operation_id}`;
                    // Show time without timezone to avoid confusion since calendar handles timezone display
                    const timePart = ` ${op.start_time}`;
                    const locPart = locationStr ? ` ${locationStr}` : '';
                    const titleParts = [`${titleLabel} • ${status}`, timePart, locPart].filter(Boolean);
                    displayTitle = titleParts.join(' | ');
                }

                console.log('[DEBUG] Title construction:', {
                    hasDescription: !!(op.description && op.description.trim()),
                    hasFlightZone: !!op.flightZone,
                    titleLabel: op.flightZone || `FLZ${op.operation_id}`,
                    timePart: ` ${op.start_time}`,
                    displayTitle: displayTitle
                });

                acc.push({
                    id: String(op.id),
                    title: displayTitle,
                    start: startZDT,
                    end: endZDT,
                    calendarId: calId,
                    description: locationStr
                        ? `Start: ${op.start_time} | Location: ${locationStr}`
                        : `Start: ${op.start_time}`,
                    location: locationStr,
                    // Store additional data for tooltip
                    _operation: op
                });

                // Console log the final event object for debugging
                const eventObj = acc[acc.length - 1];
                console.log('[SUCCESSFULLY LOADED OPERATION INTO CALENDAR]', {
                    rawOperation: op,
                    calendarEvent: {
                        id: eventObj.id,
                        title: eventObj.title,
                        startString: eventObj.start.toString(),
                        endString: eventObj.end.toString(),
                        calendarId: eventObj.calendarId
                    }
                });

            } catch (err) {
                console.error('[FAILED TO LOAD OPERATION INTO CALENDAR]', {
                    rawOperation: op,
                    error: err.message,
                    stack: err.stack
                });
            }
            return acc;
        }, []);


        // --- Calendar config ---
        console.log('[DEBUG] Creating Schedule-X calendar config...');

        // Setup event hover listeners for tooltips using event delegation
        const setupEventTooltips = (retryCount = 0) => {
            const maxRetries = 10;
            console.log('[DEBUG] Setting up event tooltips with delegation... (attempt', retryCount + 1, 'of', maxRetries + 1, ')');

            // Check if component is still mounted
            if (!calendarInstanceRef.current) {
                console.log('[DEBUG] Calendar instance not available, component may be unmounted');
                return;
            }

            if (!calendarRef.current) {
                if (retryCount < maxRetries) {
                    console.log('[DEBUG] Calendar ref not available, retrying in', (retryCount + 1) * 200, 'ms...');
                    setTimeout(() => {
                        // Check again if component is still mounted before retrying
                        if (calendarInstanceRef.current) {
                            setupEventTooltips(retryCount + 1);
                        }
                    }, (retryCount + 1) * 200);
                } else {
                    console.error('[DEBUG] Max retries reached, calendar ref still not available');
                }
                return;
            }

            console.log('[DEBUG] Calendar ref is available, checking content...');
            console.log('[DEBUG] Calendar ref innerHTML length:', calendarRef.current.innerHTML?.length || 0);

            // Check if calendar has actually rendered content
            const hasCalendarContent = calendarRef.current.querySelector('.sx__calendar, .sx__month-grid, .sx__week-grid, .sx__day-grid');
            if (!hasCalendarContent && retryCount < maxRetries) {
                console.log('[DEBUG] Calendar content not ready yet, retrying...');
                setTimeout(() => {
                    if (calendarInstanceRef.current) {
                        setupEventTooltips(retryCount + 1);
                    }
                }, (retryCount + 1) * 200);
                return;
            }

            // Remove any existing delegation listeners
            if (calendarRef.current._delegationHandler) {
                console.log('[DEBUG] Removing existing delegation listeners');
                calendarRef.current.removeEventListener('mouseover', calendarRef.current._delegationHandler);
                calendarRef.current.removeEventListener('mouseout', calendarRef.current._delegationHandler);
            }

            // Event delegation handler
            const delegationHandler = (e) => {
                // Check if the event target or any parent is a calendar event
                let eventElement = e.target.closest('.sx__event');

                // Try alternative selectors if main one doesn't work
                if (!eventElement) {
                    eventElement = e.target.closest('[class*="sx__event"], [class*="event"], .calendar-event');
                }

                if (eventElement) {
                    const isMouseEnter = e.type === 'mouseover' && !eventElement.contains(e.relatedTarget);
                    const isMouseLeave = e.type === 'mouseout' && !eventElement.contains(e.relatedTarget);

                    if (isMouseEnter) {
                        console.log('[DEBUG] Mouse enter via delegation');

                        // Find the operation for this event
                        const eventTitle = eventElement.querySelector('.sx__event-title')?.textContent ||
                            eventElement.querySelector('[class*="title"]')?.textContent ||
                            eventElement.textContent || '';

                        console.log('[DEBUG] Event title from delegation:', eventTitle);

                        // Find matching operation
                        let matchedOperation = null;
                        for (const operation of Object.values(operationsMapRef.current)) {
                            if (eventTitle.includes(`FLZ${operation.operation_id}`)) {
                                matchedOperation = operation;
                                console.log('[DEBUG] Matched operation via delegation:', operation.operation_id);
                                break;
                            }
                        }

                        // Try ID matching if title matching failed
                        if (!matchedOperation) {
                            const idMatch = eventTitle.match(/FLZ(\d+)/);
                            if (idMatch) {
                                const opId = parseInt(idMatch[1]);
                                matchedOperation = Object.values(operationsMapRef.current).find(op => op.operation_id === opId);
                                if (matchedOperation) {
                                    console.log('[DEBUG] Found operation by ID via delegation:', matchedOperation.operation_id);
                                }
                            }
                        }

                        if (matchedOperation) {
                            showTooltip(e, matchedOperation);
                        }
                    } else if (isMouseLeave) {
                        console.log('[DEBUG] Mouse leave via delegation');
                        hideTooltip();
                    }
                }
            };

            // Attach delegation listeners
            calendarRef.current.addEventListener('mouseover', delegationHandler);
            calendarRef.current.addEventListener('mouseout', delegationHandler);
            calendarRef.current._delegationHandler = delegationHandler;

            console.log('[DEBUG] Event delegation set up successfully on calendar element');
        };

        console.log('[DEBUG] Final events array passed to calendar:', events.map(e => ({
            id: e.id,
            title: e.title,
            startTime: e.start.toPlainTime().toString(),
            endTime: e.end.toPlainTime().toString(),
            startDateTime: e.start.toString(),
            endDateTime: e.end.toString()
        })));

        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        
        const calendarConfig = {
            // Use the browser's local timezone so events display properly localized to the user's location
            timezone: browserTimezone,
            views: [
                createViewMonthGrid(),
                createViewWeek(),
                createViewDay(),
                createViewMonthAgenda(),
            ],
            defaultView: createViewWeek().name,
            // Explicitly enable header with date display for week range
            header: {
                date: true,
                navigation: true,
                view: true
            },
            events,
            // Built-in timezone select plugin — adds timezone dropdown inside the calendar header
            plugins: [
                createTimezoneSelectPlugin(),
            ],
            // Merge calendar translations with timezone-select translations
            translations: mergeLocales(translations, timezoneTranslations),
            calendars: {
                authorized: {
                    colorName: 'authorized',
                    lightColors: {
                        main: '#2e7d32',
                        container: '#e8f5e9',
                        onContainer: '#1b5e20',
                    },
                },
                pending: {
                    colorName: 'pending',
                    lightColors: {
                        main: '#e65100',
                        container: '#fff3e0',
                        onContainer: '#bf360c',
                    },
                },
                complete: {
                    colorName: 'complete',
                    lightColors: {
                        main: '#1565c0',
                        container: '#e3f2fd',
                        onContainer: '#0d47a1',
                    },
                },
                deauthorized: {
                    colorName: 'deauthorized',
                    lightColors: {
                        main: '#c62828',
                        container: '#ffebee',
                        onContainer: '#b71c1c',
                    },
                },
                deleted: {
                    colorName: 'deleted',
                    lightColors: {
                        main: '#546e7a',
                        container: '#eceff1',
                        onContainer: '#37474f',
                    },
                },
                rescinded: {
                    colorName: 'rescinded',
                    lightColors: {
                        main: '#6a1b9a',
                        container: '#f3e5f5',
                        onContainer: '#4a148c',
                    },
                },
                never: {
                    colorName: 'never',
                    lightColors: {
                        main: '#795548',
                        container: '#efebe9',
                        onContainer: '#4e342e',
                    },
                },
                invalid: {
                    colorName: 'invalid',
                    lightColors: {
                        main: '#f57f17',
                        container: '#fff9c4',
                        onContainer: '#e65100',
                    },
                },
                noauth: {
                    colorName: 'noauth',
                    lightColors: {
                        main: '#388e3c',
                        container: '#f1f8e9',
                        onContainer: '#1b5e20',
                    },
                },
                note: {
                    colorName: 'note',
                    lightColors: {
                        main: '#1976d2',
                        container: '#bbdefb',
                        onContainer: '#0d47a1',
                    },
                },
            },
            callbacks: {
                onEventClick: (calEvent) => {
                    const operation = operationsMapRef.current[String(calEvent.id)];
                    if (operation && onEventClick) {
                        onEventClick(operation);
                    }
                },
                onBackgroundClick: (dateTime) => {
                    console.log('[DEBUG] onBackgroundClick called with:', dateTime);
                    if (onBackgroundClick) {
                        const formattedDateTime = extractDateTimeFromCalendarClick(dateTime);
                        onBackgroundClick(formattedDateTime);
                    }
                },
                onClickDateTime: (dateTime) => {
                    console.log('[DEBUG] onClickDateTime called with:', dateTime);
                    if (onBackgroundClick) {
                        const formattedDateTime = extractDateTimeFromCalendarClick(dateTime);
                        onBackgroundClick(formattedDateTime);
                    }
                },
                onClickDate: (date) => {
                    console.log('[DEBUG] onClickDate called with:', date);
                    if (onBackgroundClick) {
                        const formattedDateTime = extractDateTimeFromCalendarClick(date);
                        onBackgroundClick(formattedDateTime);
                    }
                },
                onDateTimeClick: (dateTime) => {
                    console.log('[DEBUG] onDateTimeClick called with:', dateTime);
                    if (onBackgroundClick) {
                        const formattedDateTime = extractDateTimeFromCalendarClick(dateTime);
                        onBackgroundClick(formattedDateTime);
                    }
                },
                onViewChange: (view) => {
                    console.log('[DEBUG] View changed to:', view);

                    // Debug header after view change
                    setTimeout(() => {
                        const headerElement = calendarRef.current?.querySelector('.sx__calendar-header');
                        const dateElement = calendarRef.current?.querySelector('.sx__calendar-header__date, [class*=\"date\"], [class*=\"Date\"]');

                        console.log('[DEBUG] Header after view change:', {
                            newView: view,
                            headerExists: !!headerElement,
                            dateElementExists: !!dateElement,
                            dateElementText: dateElement?.textContent,
                            dateElementHTML: dateElement?.outerHTML,
                            allHeaderContent: headerElement?.innerHTML
                        });

                        // With delegation, no need to re-setup tooltips - they work automatically
                        console.log('[DEBUG] View change complete - tooltips should work via delegation');
                    }, 100);
                },
                onRangeUpdate: (range) => {
                    console.log('[DEBUG] Calendar range updated - Week range detected!');
                    console.log('[DEBUG] Start date:', range.start?.toString?.());
                    console.log('[DEBUG] End date:', range.end?.toString?.());

                    // Parse the date strings to create a readable date range
                    const startStr = range.start?.toString?.();
                    const endStr = range.end?.toString?.();

                    if (startStr && endStr) {
                        const startDate = new Date(startStr.split('T')[0]);
                        const endDate = new Date(endStr.split('T')[0]);

                        const startMonth = startDate.toLocaleDateString('en-US', { month: 'short' });
                        const startDay = startDate.getDate();
                        const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' });
                        const endDay = endDate.getDate();
                        const year = endDate.getFullYear();

                        // Check if it's a single day (day view)
                        const isSingleDay = startDate.getTime() === endDate.getTime();

                        let dateRange;
                        if (isSingleDay) {
                            // For day view: "Feb 22, 2026"
                            dateRange = `${startMonth} ${startDay}, ${year}`;
                        } else {
                            // For week/month views with ranges: "Feb 16 - 22, 2026" or "Feb 16 - Mar 2, 2026"
                            dateRange = startMonth === endMonth
                                ? `${startMonth} ${startDay} - ${endDay}, ${year}`
                                : `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
                        }

                        console.log('[DEBUG] Formatted date range:', dateRange);

                        // Find and update the existing Schedule-X date display (next to navigation buttons)
                        setTimeout(() => {
                            const headerElement = calendarRef.current?.querySelector('.sx__calendar-header');

                            if (headerElement) {
                                console.log('[DEBUG] Full header HTML:', headerElement.innerHTML);

                                // Remove any custom elements we might have created before
                                const customElements = headerElement.querySelectorAll('.custom-week-range');
                                customElements.forEach(el => el.remove());

                                // Look for the actual Schedule-X date display with valid selectors
                                let dateElement = headerElement.querySelector(
                                    '.sx__date-picker-button, .sx__date-picker__button, .sx__header-date-picker, ' +
                                    '.sx__calendar-header__date-picker, .sx__calendar-header__current-date, ' +
                                    'button[data-testid*="date"], button[aria-label*="date"], button[title*="date"]'
                                );

                                // If still not found, look for any button or clickable element with date text
                                if (!dateElement) {
                                    const buttons = headerElement.querySelectorAll('button, [role="button"], .sx__button');
                                    for (let btn of buttons) {
                                        const text = btn.textContent?.trim().toLowerCase();
                                        if (text && (text.includes('2026') || text.includes('march') ||
                                            text.includes('feb') || text.includes('apr') || text.includes('jan') ||
                                            text.includes('may') || text.includes('jun') || text.includes('jul') ||
                                            text.includes('aug') || text.includes('sep') || text.includes('oct') ||
                                            text.includes('nov') || text.includes('dec'))) {
                                            dateElement = btn;
                                            console.log('[DEBUG] Found Schedule-X date button:', text);
                                            break;
                                        }
                                    }
                                }

                                // Last resort: find any element that contains year text
                                if (!dateElement) {
                                    const allElements = headerElement.querySelectorAll('*');
                                    for (let el of allElements) {
                                        const text = el.textContent?.trim();
                                        if (text && text.includes('2026') && !el.querySelector('*')) { // leaf element only
                                            dateElement = el;
                                            console.log('[DEBUG] Found date element (last resort):', text);
                                            break;
                                        }
                                    }
                                }

                                if (dateElement) {
                                    const originalText = dateElement.textContent;
                                    console.log('[DEBUG] Found existing Schedule-X date element:', originalText);
                                    console.log('[DEBUG] Element classes:', dateElement.className);
                                    console.log('[DEBUG] Updating with date range:', dateRange);

                                    // Update the existing Schedule-X date element
                                    dateElement.textContent = dateRange;
                                } else {
                                    console.log('[DEBUG] Could not find Schedule-X date element');
                                    console.log('[DEBUG] Available buttons in header:',
                                        Array.from(headerElement.querySelectorAll('button, [role="button"]'))
                                            .map(b => ({ text: b.textContent?.trim(), classes: b.className }))
                                    );
                                }
                            }
                        }, 50); // Increased timeout to ensure Schedule-X has rendered
                    }
                },
            },
        };

        console.log('[DEBUG] Calendar config created:', {
            timezone: calendarConfig.timezone,
            defaultView: calendarConfig.defaultView,
            headerConfig: calendarConfig.header,
            viewsCount: calendarConfig.views.length,
            eventsCount: events.length
        });

        // Destroy previous instance if any
        if (calendarInstanceRef.current) {
            try { calendarInstanceRef.current.destroy?.(); } catch (_) { }
            calendarInstanceRef.current = null;
            calendarRef.current.innerHTML = '';
        }

        // Create + render new calendar
        console.log('[DEBUG] Creating Schedule-X calendar instance...');
        const cal = createCalendar(calendarConfig);
        console.log('[DEBUG] Calendar instance created:', cal);

        console.log('[DEBUG] Rendering calendar to DOM element:', calendarRef.current);
        console.log('[DEBUG] Calendar ref element children before render:', calendarRef.current?.children?.length || 0);
        cal.render(calendarRef.current);

        console.log('[DEBUG] Calendar rendered successfully');
        console.log('[DEBUG] Calendar ref element children after render:', calendarRef.current?.children?.length || 0);
        calendarInstanceRef.current = cal;

        // Add custom DOM event listener for more reliable date detection
        setTimeout(() => {
            if (calendarRef.current && onBackgroundClick) {
                const handleCalendarClick = (event) => {
                    // Don't intercept clicks on existing events
                    if (event.target.closest('.sx__event, .sx__event-title, [class*="event"]')) {
                        return;
                    }

                    // Don't intercept clicks on specific UI controls (be more specific to avoid false matches)
                    const isUIControl = event.target.matches('input, button, select') || 
                        event.target.closest('.sx__date-input, .sx__header, .sx__navigation, .sx__view-selection, .sx__timezone-select') ||
                        event.target.closest('input[class*="input"], button[class*="button"], select[class*="select"]');
                    
                    if (isUIControl) {
                        console.log('[DEBUG] Click on UI control ignored:', event.target.className);
                        return;
                    }

                    console.log('[DEBUG] Processing calendar click, target:', {
                        className: event.target.className,
                        tagName: event.target.tagName,
                        dataset: event.target.dataset
                    });

                    // Look for calendar grid areas - be more specific about valid click targets
                    const dateCell = event.target.closest('.sx__time-grid-day, .sx__date-cell, .sx__time-slot, .sx__calendar-cell, [data-time-grid-date], [data-date]');
                    
                    console.log('[DEBUG] Calendar cell search result:', {
                        dateCell: dateCell,
                        dateCellClass: dateCell?.className,
                        clickedElement: event.target.className,
                        foundCell: !!dateCell
                    });
                    
                    if (dateCell) {
                        console.log('[DEBUG] Valid calendar grid click detected on:', dateCell);
                        console.log('[DEBUG] Cell attributes:', {
                            className: dateCell.className,
                            dataset: dateCell.dataset,
                            textContent: dateCell.textContent?.trim(),
                            tagName: dateCell.tagName
                        });

                        // Also check for time-related elements
                        const timeSlot = event.target.closest('.sx__time-grid-day, .sx__time-slot, [data-time], [data-time-grid-date], [class*="time"]:not([class*="input"]):not([class*="select"])');
                        const hourElement = event.target.closest('[data-hour], [class*="hour"]:not([class*="input"])');
                        
                        console.log('[DEBUG] Time-related elements:', {
                            timeSlot: timeSlot?.className,
                            timeSlotDataset: timeSlot?.dataset,
                            hourElement: hourElement?.className,
                            hourElementDataset: hourElement?.dataset,
                            clickedElement: {
                                className: event.target.className,
                                dataset: event.target.dataset,
                                textContent: event.target.textContent?.trim()
                            }
                        });

                        // Try to extract date from data attributes
                        let clickedDate = null;
                        let clickedTime = null;
                        
                        console.log('[DEBUG] All dataset keys:', Object.keys(dateCell.dataset));
                        console.log('[DEBUG] Full dataset:', dateCell.dataset);
                        
                        console.log('[DEBUG] All dataset keys:', Object.keys(dateCell.dataset));
                        console.log('[DEBUG] Full dataset:', dateCell.dataset);
                        
                        // Also log datasets from related elements
                        if (timeSlot && timeSlot !== dateCell) {
                            console.log('[DEBUG] TimeSlot dataset:', Object.keys(timeSlot.dataset), timeSlot.dataset);
                        }
                        if (event.target.dataset && Object.keys(event.target.dataset).length > 0) {
                            console.log('[DEBUG] Clicked element dataset:', Object.keys(event.target.dataset), event.target.dataset);
                        }
                        
                        // Check for Schedule-X specific attributes first
                        if (dateCell.dataset.timeGridDate) {
                            clickedDate = dateCell.dataset.timeGridDate;
                            console.log('[DEBUG] Found timeGridDate:', clickedDate);
                        }
                        // Check for standard data-date attribute
                        else if (dateCell.dataset.date) {
                            clickedDate = dateCell.dataset.date;
                            console.log('[DEBUG] Found date:', clickedDate);
                        }
                        
                        // Try to extract time from various sources - check Schedule-X specific attributes first
                        const allTimeElements = [event.target, timeSlot, hourElement, dateCell].filter(Boolean);
                        
                        for (const element of allTimeElements) {
                            if (!clickedTime && element?.dataset) {
                                console.log('[DEBUG] Checking element for time:', element.tagName, Object.keys(element.dataset));
                                
                                // Check for Schedule-X specific time attributes
                                if (element.dataset.timeGridTime) {
                                    clickedTime = element.dataset.timeGridTime;
                                    console.log('[DEBUG] Found time from timeGridTime:', clickedTime);
                                    break;
                                }
                                
                                if (element.dataset.timeGridHour !== undefined) {
                                    const hour = String(element.dataset.timeGridHour).padStart(2, '0');
                                    const minute = element.dataset.timeGridMinute || '00';
                                    clickedTime = `${hour}:${String(minute).padStart(2, '0')}`;
                                    console.log('[DEBUG] Found time from timeGridHour/minute:', clickedTime);
                                    break;
                                }
                                
                                // Check for direct time attributes
                                if (element.dataset.time) {
                                    clickedTime = element.dataset.time;
                                    console.log('[DEBUG] Found time from data-time:', clickedTime);
                                    break;
                                }
                                
                                // Check for hour attributes
                                if (element.dataset.hour !== undefined) {
                                    const hour = String(element.dataset.hour).padStart(2, '0');
                                    const minute = element.dataset.minute || '00';
                                    clickedTime = `${hour}:${String(minute).padStart(2, '0')}`;
                                    console.log('[DEBUG] Found time from hour/minute:', clickedTime);
                                    break;
                                }
                                
                                // Check for any other time-related attributes
                                const timeKeys = Object.keys(element.dataset).filter(key => 
                                    key.toLowerCase().includes('time') || 
                                    key.toLowerCase().includes('hour') || 
                                    key.toLowerCase().includes('minute')
                                );
                                
                                if (timeKeys.length > 0) {
                                    console.log('[DEBUG] Found time-related dataset keys:', timeKeys, 'values:', 
                                        timeKeys.reduce((acc, key) => { acc[key] = element.dataset[key]; return acc; }, {}));
                                }
                            }
                        }
                        
                        // Try to extract time from element position or text content
                        if (!clickedTime) {
                            // Look for time in text content (e.g., "14:00", "2:00 PM")
                            const timePattern = /(\d{1,2}):(\d{2})/;
                            for (const element of allTimeElements) {
                                const text = element?.textContent?.trim() || '';
                                const timeMatch = text.match(timePattern);
                                if (timeMatch) {
                                    const [, hour, minute] = timeMatch;
                                    clickedTime = `${String(hour).padStart(2, '0')}:${minute}`;
                                    console.log('[DEBUG] Found time from text content:', clickedTime, 'in element:', text);
                                    break;
                                }
                            }
                        }
                        
                        // Try to calculate time from mouse position within time grid
                        if (!clickedTime && dateCell.classList.contains('sx__time-grid-day')) {
                            try {
                                const rect = dateCell.getBoundingClientRect();
                                const clickY = event.clientY - rect.top;
                                const cellHeight = rect.height;
                                
                                // Assuming 24-hour day (0-23 hours)
                                const hourProgress = Math.max(0, Math.min(1, clickY / cellHeight));
                                const totalMinutes = Math.floor(hourProgress * 24 * 60);
                                const hours = Math.floor(totalMinutes / 60);
                                const minutes = Math.floor((totalMinutes % 60) / 15) * 15; // Round to 15-minute intervals
                                
                                clickedTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                                console.log('[DEBUG] Calculated time from position:', clickedTime, 'Y:', clickY, 'Height:', cellHeight);
                            } catch (e) {
                                console.log('[DEBUG] Failed to calculate time from position:', e);
                            }
                        }
                        
                        // Fallback to constructed date from parts or text
                        if (!clickedDate) {
                            // Check for other possible date attributes
                            if (dateCell.dataset.day || dateCell.dataset.month || dateCell.dataset.year) {
                                const year = dateCell.dataset.year || new Date().getFullYear();
                                const month = String(dateCell.dataset.month || new Date().getMonth() + 1).padStart(2, '0');
                                const day = String(dateCell.dataset.day || dateCell.textContent?.trim()).padStart(2, '0');
                                if (day && !isNaN(day) && day !== '00') {
                                    clickedDate = `${year}-${month}-${day}`;
                                    console.log('[DEBUG] Constructed date from parts:', clickedDate);
                                }
                            }
                            // Try to parse date from text content if it's a number (day of month)
                            else {
                                console.log('[DEBUG] Trying to extract from text content:', dateCell.textContent?.trim());
                                const dayText = dateCell.textContent?.trim();
                                if (dayText && /^\d{1,2}$/.test(dayText)) {
                                    const day = String(parseInt(dayText)).padStart(2, '0');
                                    const now = new Date();
                                    const year = now.getFullYear();
                                    const month = String(now.getMonth() + 1).padStart(2, '0');
                                    clickedDate = `${year}-${month}-${day}`;
                                    console.log('[DEBUG] Constructed date from day text:', clickedDate);
                                }
                            }
                        }

                        if (clickedDate) {
                            console.log('[DEBUG] Extracted date from DOM:', clickedDate);
                            
                            // Use extracted time if found, otherwise use current time
                            const finalTime = clickedTime || new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                            console.log('[DEBUG] Using time:', finalTime, clickedTime ? '(extracted)' : '(current)');
                            
                            const formattedDateTime = `${clickedDate} ${finalTime}`;
                            console.log('[DEBUG] Final formatted date/time:', formattedDateTime);
                            onBackgroundClick(formattedDateTime);
                        } else {
                            console.log('[DEBUG] Could not extract date from clicked cell, using current date');
                            // Fallback to current behavior
                            const formattedDateTime = extractDateTimeFromCalendarClick(null);
                            onBackgroundClick(formattedDateTime);
                        }
                    }
                };

                calendarRef.current.addEventListener('click', handleCalendarClick);
                calendarRef.current._customClickHandler = handleCalendarClick;
            }
        }, 200);

        // Debug: Check header elements after render
        setTimeout(() => {
            const headerElement = calendarRef.current?.querySelector('.sx__calendar-header');
            const dateElement = calendarRef.current?.querySelector('.sx__calendar-header__date, [class*="date"], [class*="Date"]');

            console.log('[DEBUG] Header DOM inspection:', {
                headerExists: !!headerElement,
                headerHTML: headerElement?.outerHTML,
                dateElementExists: !!dateElement,
                dateElementHTML: dateElement?.outerHTML,
                allHeaderChildren: headerElement ? Array.from(headerElement.children).map(child => child.className) : [],
                currentView: cal.getView?.()?.name || 'unknown'
            });

            // Check all possible date-related elements
            const allPossibleDateElements = calendarRef.current?.querySelectorAll('*[class*="date"], *[class*="Date"], *[class*="range"], *[class*="Range"]');
            console.log('[DEBUG] All possible date elements:', Array.from(allPossibleDateElements || []).map(el => ({
                tagName: el.tagName,
                className: el.className,
                textContent: el.textContent?.trim(),
                innerHTML: el.innerHTML
            })));

            // Check if switching to week view shows the date range
            console.log('[DEBUG] Current view after render:', cal.getView?.()?.name);
        }, 100);

        return () => {
            console.log('[DEBUG] Cleaning up calendar instance...');
            hideTooltip(); // Clean up tooltip

            // Clean up delegation listeners
            if (calendarRef.current?._delegationHandler) {
                calendarRef.current.removeEventListener('mouseover', calendarRef.current._delegationHandler);
                calendarRef.current.removeEventListener('mouseout', calendarRef.current._delegationHandler);
                delete calendarRef.current._delegationHandler;
            }

            // Clean up custom click handler
            if (calendarRef.current?._customClickHandler) {
                calendarRef.current.removeEventListener('click', calendarRef.current._customClickHandler);
                delete calendarRef.current._customClickHandler;
            }

            try { calendarInstanceRef.current?.destroy?.(); } catch (_) { }
            calendarInstanceRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operations]);

    return (
        <Box sx={{ width: '100%' }}>
            {/* Debug info - remove in production */}
            {process.env.NODE_ENV === 'development' && (
                <Box sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: '12px' }}>
                    <Typography variant="caption">
                        Debug: {operations.length} operations loaded, {Object.keys(operationsMapRef.current).length} in map
                        {tooltip.visible ? ' | Tooltip visible' : ' | Tooltip hidden'}
                    </Typography>
                </Box>
            )}

            {/* Legend */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                {[
                    { id: 'authorized', label: 'Authorized', color: '#2e7d32' },
                    { id: 'pending', label: 'Pending', color: '#e65100' },
                    { id: 'complete', label: 'Complete', color: '#1565c0' },
                    { id: 'deauthorized', label: 'De-authorized', color: '#c62828' },
                    { id: 'rescinded', label: 'Rescinded', color: '#6a1b9a' },
                    { id: 'deleted', label: 'Deleted', color: '#546e7a' },
                    { id: 'never', label: 'Never Authorized', color: '#795548' },
                    { id: 'invalid', label: 'Invalid Detected', color: '#f57f17' },
                    { id: 'noauth', label: 'No Auth Required', color: '#388e3c' },
                ].map(({ id, label, color }) => (
                    <Box key={id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                    </Box>
                ))}
            </Box>

            {/* Schedule-X calendar mount point */}
            <Box
                ref={calendarRef}
                sx={{
                    width: '100%',
                    height: { xs: '500px', sm: '600px', md: '680px' },
                    maxHeight: { xs: '70vh', sm: '75vh', md: '80vh' },
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid #e0e3e8',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                    '& .sx__event': {
                        cursor: 'pointer !important',
                        transition: 'transform 0.2s ease !important',
                    },
                    '& .sx__event:hover': {
                        transform: 'scale(1.02) !important',
                        zIndex: 10,
                    },
                    // Make grid cells look clickable
                    '& .sx__time-grid-day, & .sx__month-grid-day, & .sx__day-grid-day': {
                        cursor: 'cell !important',
                    }
                }}
            />

            {/* Event Tooltip */}
            {tooltip.visible && tooltip.content && (
                <>
                    {console.log('[DEBUG] Rendering tooltip at position:', tooltip.x, tooltip.y)}
                    <Box
                        sx={{
                            position: 'fixed',
                            left: tooltip.x,
                            top: tooltip.y,
                            zIndex: 10000,
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            color: 'white',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                            fontSize: '13px',
                            lineHeight: 1.4,
                            maxWidth: '320px',
                            minWidth: '250px',
                            pointerEvents: 'none',
                            animation: 'fadeIn 0.2s ease-out',
                        }}
                    >
                        <Box sx={{ marginBottom: '8px' }}>
                            <Typography variant="subtitle2" sx={{ color: '#4fc3f7', fontSize: '14px', fontWeight: 600 }}>
                                {tooltip.content.flightZone}
                            </Typography>
                            <Typography variant="caption" sx={{
                                color: tooltip.content.status === 'Authorized' ? '#81c784' :
                                    tooltip.content.status === 'Pending' ? '#ffb74d' :
                                        tooltip.content.status === 'Complete' ? '#64b5f6' : '#f06292',
                                fontSize: '12px',
                                fontWeight: 500
                            }}>
                                {tooltip.content.status}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                <Typography variant="caption" sx={{ color: '#b0bec5', minWidth: '60px', fontSize: '12px' }}>
                                    Time:
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'white', fontSize: '12px' }}>
                                    {tooltip.content.startTime} ({tooltip.content.duration})
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                <Typography variant="caption" sx={{ color: '#b0bec5', minWidth: '60px', fontSize: '12px' }}>
                                    Location:
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'white', fontSize: '12px', wordBreak: 'break-word' }}>
                                    {tooltip.content.location}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                <Typography variant="caption" sx={{ color: '#b0bec5', minWidth: '60px', fontSize: '12px' }}>
                                    Pilot:
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'white', fontSize: '12px' }}>
                                    {tooltip.content.pilot}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                <Typography variant="caption" sx={{ color: '#b0bec5', minWidth: '60px', fontSize: '12px' }}>
                                    Aircraft:
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'white', fontSize: '12px' }}>
                                    {tooltip.content.aircraft}
                                </Typography>
                            </Box>

                            {tooltip.content.description && tooltip.content.description !== 'No description' && (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                                    <Typography variant="caption" sx={{ color: '#b0bec5', minWidth: '60px', fontSize: '12px' }}>
                                        Details:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'white', fontSize: '12px', wordBreak: 'break-word' }}>
                                        {tooltip.content.description}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default OperationCalendarView;
